import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAudioRecognition } from '../AudioRecognitionContext';
import type { RecognitionFrame } from '../types';
import { hzToMidi, midiToName } from '../util/note';

const MAX_DIAGNOSTIC_FRAMES = 240;
const MAX_PEAKS_PER_FRAME = 12;
const MAX_HEATMAP_COLUMNS = 72;
const MAX_HEATMAP_ROWS = 12;
const MAX_CLIPBOARD_HEATMAP_COLUMNS = 36;
const MAX_CLIPBOARD_PEAK_SUMMARY = 12;
const UI_UPDATE_INTERVAL_MS = 140;

type CalibrationDiagnosticPeak = {
  hz: number;
  midi: number;
  note: string;
  amp: number;
  score?: number;
};

type CalibrationDiagnosticFrame = {
  capturedAtMs: number;
  capturedAtIso: string;
  timeSec: number;
  rms: number;
  minRms: number;
  noiseFloor: number;
  peaks: CalibrationDiagnosticPeak[];
  waveform?: number[];
};

type CalibrationPeakSummary = {
  midi: number;
  note: string;
  hz: number;
  maxAmp: number;
  avgAmp: number;
  count: number;
};

type DiagnosticsViewState = {
  frames: CalibrationDiagnosticFrame[];
  captureFrames: CalibrationDiagnosticFrame[];
};

const formatNumber = (value: number, fractionDigits = 4) =>
  Number.isFinite(value) ? value.toFixed(fractionDigits) : 'n/a';

const serializeWaveform = (waveform: Float32Array | undefined) => {
  if (!waveform || waveform.length === 0) return undefined;
  const stride = Math.max(1, Math.ceil(waveform.length / 96));
  const serialized: number[] = [];

  for (let index = 0; index < waveform.length; index += stride) {
    serialized.push(Number(waveform[index].toFixed(5)));
    if (serialized.length >= 96) break;
  }

  return serialized;
};

const toDiagnosticFrame = (frame: RecognitionFrame): CalibrationDiagnosticFrame => {
  const capturedAtMs = Date.now();
  return {
    capturedAtMs,
    capturedAtIso: new Date(capturedAtMs).toISOString(),
    timeSec: frame.timeSec,
    rms: frame.rms ?? 0,
    minRms: frame.minRms ?? 0,
    noiseFloor: frame.noiseFloor ?? 0,
    peaks: (frame.peaks ?? [])
      .filter((peak) => Number.isFinite(peak.hz) && peak.hz > 0 && Number.isFinite(peak.amp))
      .map((peak) => {
        const midi = Math.round(hzToMidi(peak.hz));
        return {
          hz: peak.hz,
          midi,
          note: midiToName(midi),
          amp: peak.amp,
          score: peak.score,
        };
      })
      .sort((a, b) => b.amp - a.amp || a.hz - b.hz)
      .slice(0, MAX_PEAKS_PER_FRAME),
    waveform: serializeWaveform(frame.waveform),
  };
};

const buildPeakSummary = (frames: CalibrationDiagnosticFrame[]): CalibrationPeakSummary[] => {
  const grouped = new Map<
    number,
    {
      midi: number;
      note: string;
      hzAtMaxAmp: number;
      maxAmp: number;
      ampSum: number;
      count: number;
    }
  >();

  frames.forEach((frame) => {
    frame.peaks.forEach((peak) => {
      const existing = grouped.get(peak.midi);
      if (!existing) {
        grouped.set(peak.midi, {
          midi: peak.midi,
          note: peak.note,
          hzAtMaxAmp: peak.hz,
          maxAmp: peak.amp,
          ampSum: peak.amp,
          count: 1,
        });
        return;
      }

      existing.ampSum += peak.amp;
      existing.count += 1;
      if (peak.amp > existing.maxAmp) {
        existing.maxAmp = peak.amp;
        existing.hzAtMaxAmp = peak.hz;
      }
    });
  });

  return Array.from(grouped.values())
    .map((group) => ({
      midi: group.midi,
      note: group.note,
      hz: group.hzAtMaxAmp,
      maxAmp: group.maxAmp,
      avgAmp: group.ampSum / Math.max(group.count, 1),
      count: group.count,
    }))
    .sort((a, b) => b.maxAmp - a.maxAmp || a.midi - b.midi);
};

const copyTextWithFallback = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
};

const downloadJson = (value: unknown, filename: string) => {
  const blob = new Blob([JSON.stringify(value, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const downsampleFrames = (
  frames: CalibrationDiagnosticFrame[],
  maxColumns = MAX_HEATMAP_COLUMNS,
) => {
  if (frames.length <= maxColumns) return frames;
  const stride = Math.ceil(frames.length / maxColumns);
  return frames.filter((_, index) => index % stride === 0).slice(-maxColumns);
};

const getPeakAmpForMidi = (frame: CalibrationDiagnosticFrame, midi: number) =>
  frame.peaks.reduce((max, peak) => (peak.midi === midi ? Math.max(max, peak.amp) : max), 0);

const formatFrameGate = (frame: CalibrationDiagnosticFrame | null) => {
  if (!frame) return 'none';
  const gateState = frame.rms >= frame.minRms ? 'above minRms' : 'below minRms';
  return `rms ${formatNumber(frame.rms, 6)} / min ${formatNumber(
    frame.minRms,
    6,
  )} (${gateState}), noise ${formatNumber(frame.noiseFloor, 8)}, peaks ${frame.peaks.length}`;
};

const formatLatestPeaks = (frame: CalibrationDiagnosticFrame | null) => {
  const lines = ['Latest frame peaks:'];
  if (!frame || frame.peaks.length === 0) return [...lines, '  none'];

  return [
    ...lines,
    ...frame.peaks
      .slice(0, 8)
      .map(
        (peak, index) =>
          `  ${index + 1}. ${peak.note} midi ${peak.midi} ${formatNumber(
            peak.hz,
            1,
          )} Hz amp ${formatNumber(peak.amp, 5)}`,
      ),
  ];
};

const formatPeakSummary = (
  title: string,
  peaks: CalibrationPeakSummary[],
  limit = MAX_CLIPBOARD_PEAK_SUMMARY,
) => {
  const lines = [`${title}:`];
  if (peaks.length === 0) return [...lines, '  none'];

  return [
    ...lines,
    ...peaks
      .slice(0, limit)
      .map(
        (peak, index) =>
          `  ${index + 1}. ${peak.note} midi ${peak.midi} ${formatNumber(
            peak.hz,
            1,
          )} Hz max ${formatNumber(peak.maxAmp, 5)} avg ${formatNumber(
            peak.avgAmp,
            5,
          )} frames ${peak.count}`,
      ),
  ];
};

const ampToHeatmapCell = (amp: number, maxAmp: number) => {
  if (!Number.isFinite(amp) || amp <= 0) return '.';
  const intensity = Math.max(0, Math.min(1, amp / Math.max(maxAmp, 0.000001)));
  return String(Math.max(1, Math.ceil(intensity * 9)));
};

const formatHeatmapNoteLabel = (note: string) => `${note}    `.slice(0, 4);

const formatHeatmapForClipboard = (
  label: string,
  frames: CalibrationDiagnosticFrame[],
  rows: CalibrationPeakSummary[],
  maxAmp: number,
) => {
  const columns = downsampleFrames(frames, MAX_CLIPBOARD_HEATMAP_COLUMNS);
  const heatmapRows = rows.slice(0, MAX_HEATMAP_ROWS);
  const lines = [
    `Heatmap (${label}; ${heatmapRows.length} rows x ${columns.length} columns; . none, 1-9 relative amp):`,
  ];

  if (heatmapRows.length === 0 || columns.length === 0) return [...lines, '  none'];

  return [
    ...lines,
    ...heatmapRows.map((row) => {
      const cells = columns
        .map((frame) => ampToHeatmapCell(getPeakAmpForMidi(frame, row.midi), maxAmp))
        .join('');
      return `  ${formatHeatmapNoteLabel(row.note)} ${cells} max ${formatNumber(
        row.maxAmp,
        5,
      )} frames ${row.count}`;
    }),
  ];
};

const buildClipboardText = ({
  capturedAtIso,
  calibration,
  latestFrame,
  rollingFrameCount,
  captureFrameCount,
  rollingSummary,
  captureSummary,
  heatmapFrames,
  heatmapRows,
  heatmapMaxAmp,
}: {
  capturedAtIso: string;
  calibration: {
    phase: string;
    stageLabel: string;
    instruction: string;
    detail: string;
    heardNotes: string[];
    stepIndex: number;
    totalSteps: number;
    sampleCount: number;
  };
  latestFrame: CalibrationDiagnosticFrame | null;
  rollingFrameCount: number;
  captureFrameCount: number;
  rollingSummary: CalibrationPeakSummary[];
  captureSummary: CalibrationPeakSummary[];
  heatmapFrames: CalibrationDiagnosticFrame[];
  heatmapRows: CalibrationPeakSummary[];
  heatmapMaxAmp: number;
}) =>
  [
    'Calibration audio diagnostics',
    `Captured: ${capturedAtIso}`,
    `Phase: ${calibration.phase}; stage: ${calibration.stageLabel}; step ${calibration.stepIndex}/${calibration.totalSteps}; samples ${calibration.sampleCount}`,
    `Instruction: ${calibration.instruction}`,
    `Detail: ${calibration.detail}`,
    `Heard: ${calibration.heardNotes.length > 0 ? calibration.heardNotes.join(', ') : 'none'}`,
    `Frames: rolling ${rollingFrameCount}; capture ${captureFrameCount}`,
    `Latest gate: ${formatFrameGate(latestFrame)}`,
    '',
    ...formatLatestPeaks(latestFrame),
    '',
    ...formatPeakSummary('Capture peak summary', captureSummary),
    '',
    ...formatPeakSummary('Rolling peak summary', rollingSummary, 8),
    '',
    ...formatHeatmapForClipboard(
      captureFrameCount > 0 ? 'capture window' : 'rolling window',
      heatmapFrames,
      heatmapRows,
      heatmapMaxAmp,
    ),
    '',
    'Raw frame arrays and waveform samples are omitted from clipboard output. Use Export JSON for the full capture.',
  ].join('\n');

const panelStyles = {
  marginTop: '14px',
  padding: '12px',
  borderRadius: '14px',
  border: '1px solid rgba(121, 216, 255, 0.22)',
  backgroundColor: 'rgba(3, 8, 16, 0.48)',
};

const titleStyles = {
  color: '#F2F6FF',
  fontSize: '13px',
  fontWeight: 900,
  letterSpacing: '0.02em',
};

const secondaryTextStyles = {
  color: 'rgba(226, 234, 255, 0.68)',
  fontSize: '11px',
  lineHeight: 1.45,
};

const summaryBoxStyles = {
  minWidth: 0,
  padding: '10px',
  borderRadius: '12px',
  border: '1px solid rgba(226, 234, 255, 0.1)',
  backgroundColor: 'rgba(255, 255, 255, 0.035)',
};

const actionButtonStyles = {
  minHeight: '34px',
  paddingX: '12px',
  borderRadius: '999px',
  borderColor: 'rgba(226, 234, 255, 0.18)',
  color: '#EAF2FF',
  fontSize: '12px',
  fontWeight: 800,
  textTransform: 'none',
  '&:hover': {
    borderColor: 'rgba(121, 216, 255, 0.44)',
    backgroundColor: 'rgba(121, 216, 255, 0.08)',
  },
};

const CalibrationDiagnosticsPanel = () => {
  const { addListener, removeListener, calibration } = useAudioRecognition();
  const framesRef = useRef<CalibrationDiagnosticFrame[]>([]);
  const captureFramesRef = useRef<CalibrationDiagnosticFrame[]>([]);
  const lastUiUpdateMsRef = useRef(0);
  const calibrationStageRef = useRef(calibration.stageLabel);
  const calibrationRunningRef = useRef(calibration.running);
  const previousRunningRef = useRef(calibration.running);
  const previousStepKeyRef = useRef(`${calibration.stepIndex}:${calibration.stageLabel}`);
  const [copyStatus, setCopyStatus] = useState('');
  const [viewState, setViewState] = useState<DiagnosticsViewState>({
    frames: [],
    captureFrames: [],
  });

  useEffect(() => {
    calibrationStageRef.current = calibration.stageLabel;
    calibrationRunningRef.current = calibration.running;

    const stepKey = `${calibration.stepIndex}:${calibration.stageLabel}`;
    if (calibration.running && !previousRunningRef.current) {
      framesRef.current = [];
      captureFramesRef.current = [];
      setViewState({ frames: [], captureFrames: [] });
    } else if (calibration.stageLabel === 'Capture' && stepKey !== previousStepKeyRef.current) {
      captureFramesRef.current = [];
      setViewState((current) => ({ ...current, captureFrames: [] }));
    }

    previousRunningRef.current = calibration.running;
    previousStepKeyRef.current = stepKey;
  }, [calibration.running, calibration.stageLabel, calibration.stepIndex]);

  useEffect(() => {
    const listener = {
      name: 'calibration-diagnostics-listener',
      wantsWaveform: true,
      onFrame: (frame: RecognitionFrame) => {
        const diagnosticFrame = toDiagnosticFrame(frame);
        framesRef.current = [...framesRef.current, diagnosticFrame].slice(-MAX_DIAGNOSTIC_FRAMES);

        if (calibrationRunningRef.current && calibrationStageRef.current === 'Capture') {
          captureFramesRef.current = [...captureFramesRef.current, diagnosticFrame].slice(
            -MAX_DIAGNOSTIC_FRAMES,
          );
        }

        const nowMs = Date.now();
        if (nowMs - lastUiUpdateMsRef.current >= UI_UPDATE_INTERVAL_MS) {
          lastUiUpdateMsRef.current = nowMs;
          setViewState({
            frames: framesRef.current,
            captureFrames: captureFramesRef.current,
          });
        }
      },
    };

    addListener(listener);
    return () => {
      removeListener(listener);
    };
  }, [addListener, removeListener]);

  const latestFrame = viewState.frames[viewState.frames.length - 1] ?? null;
  const rollingSummary = useMemo(() => buildPeakSummary(viewState.frames), [viewState.frames]);
  const captureSummary = useMemo(
    () => buildPeakSummary(viewState.captureFrames),
    [viewState.captureFrames],
  );
  const heatmapFrames =
    viewState.captureFrames.length > 0 ? viewState.captureFrames : viewState.frames;
  const heatmapColumns = useMemo(() => downsampleFrames(heatmapFrames), [heatmapFrames]);
  const heatmapRows = useMemo(
    () => buildPeakSummary(heatmapFrames).slice(0, MAX_HEATMAP_ROWS),
    [heatmapFrames],
  );
  const heatmapMaxAmp = Math.max(...heatmapRows.map((row) => row.maxAmp), 0.000001);
  const capturedAtIso = useMemo(() => new Date().toISOString(), [viewState]);
  const fullPayload = useMemo(
    () => ({
      capturedAtIso,
      schema: 'calibration-audio-diagnostics-full-v1',
      clipboardNote: 'Use Copy summary for a compact paste-friendly report.',
      calibration: {
        phase: calibration.phase,
        stageLabel: calibration.stageLabel,
        instruction: calibration.instruction,
        detail: calibration.detail,
        heardNotes: calibration.heardNotes,
        stepIndex: calibration.stepIndex,
        totalSteps: calibration.totalSteps,
        sampleCount: calibration.sampleCount,
      },
      latestFrame,
      rollingPeakSummary: rollingSummary.slice(0, 16),
      capturePeakSummary: captureSummary.slice(0, 16),
      rollingFrames: viewState.frames,
      captureFrames: viewState.captureFrames,
    }),
    [calibration, capturedAtIso, captureSummary, latestFrame, rollingSummary, viewState],
  );
  const clipboardText = useMemo(
    () =>
      buildClipboardText({
        capturedAtIso,
        calibration: {
          phase: calibration.phase,
          stageLabel: calibration.stageLabel,
          instruction: calibration.instruction,
          detail: calibration.detail,
          heardNotes: calibration.heardNotes,
          stepIndex: calibration.stepIndex,
          totalSteps: calibration.totalSteps,
          sampleCount: calibration.sampleCount,
        },
        latestFrame,
        rollingFrameCount: viewState.frames.length,
        captureFrameCount: viewState.captureFrames.length,
        rollingSummary,
        captureSummary,
        heatmapFrames,
        heatmapRows,
        heatmapMaxAmp,
      }),
    [
      capturedAtIso,
      calibration.phase,
      calibration.stageLabel,
      calibration.instruction,
      calibration.detail,
      calibration.heardNotes,
      calibration.stepIndex,
      calibration.totalSteps,
      calibration.sampleCount,
      latestFrame,
      viewState.frames.length,
      viewState.captureFrames.length,
      rollingSummary,
      captureSummary,
      heatmapFrames,
      heatmapRows,
      heatmapMaxAmp,
    ],
  );

  const clearCopyStatus = useCallback(() => {
    window.setTimeout(() => setCopyStatus(''), 1600);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await copyTextWithFallback(clipboardText);
      setCopyStatus('Copied');
      clearCopyStatus();
    } catch (error) {
      setCopyStatus(error instanceof Error ? error.message : 'Copy failed');
      clearCopyStatus();
    }
  }, [clearCopyStatus, clipboardText]);

  const handleDownload = useCallback(() => {
    downloadJson(
      fullPayload,
      `calibration-audio-diagnostics-${fullPayload.capturedAtIso.replace(/[:.]/g, '-')}.json`,
    );
    setCopyStatus('Downloaded');
    clearCopyStatus();
  }, [clearCopyStatus, fullPayload]);

  const heatmapWidth = 620;
  const heatmapHeight = 230;
  const labelWidth = 58;
  const topPadding = 18;
  const bottomPadding = 24;
  const cellGap = 2;
  const gridWidth = heatmapWidth - labelWidth - 12;
  const gridHeight = heatmapHeight - topPadding - bottomPadding;
  const cellWidth =
    heatmapColumns.length > 0
      ? Math.max(
          3,
          (gridWidth - cellGap * Math.max(0, heatmapColumns.length - 1)) / heatmapColumns.length,
        )
      : 0;
  const cellHeight =
    heatmapRows.length > 0
      ? Math.max(
          8,
          (gridHeight - cellGap * Math.max(0, heatmapRows.length - 1)) / heatmapRows.length,
        )
      : 0;

  return (
    <Box sx={panelStyles}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '10px',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '10px',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={titleStyles}>Calibration audio diagnostics</Box>
          <Box sx={secondaryTextStyles}>
            {latestFrame
              ? `RMS ${formatNumber(latestFrame.rms, 6)} · min ${formatNumber(
                  latestFrame.minRms,
                  6,
                )} · frames ${viewState.frames.length} · capture ${viewState.captureFrames.length}`
              : 'Waiting for microphone frames'}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            sx={actionButtonStyles}
          >
            Copy summary
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownload}
            sx={actionButtonStyles}
          >
            Export JSON
          </Button>
          {copyStatus && <Box sx={secondaryTextStyles}>{copyStatus}</Box>}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1fr' },
          gap: '10px',
          marginBottom: '10px',
        }}
      >
        <Box sx={summaryBoxStyles}>
          <Box sx={titleStyles}>Latest peaks</Box>
          {(latestFrame?.peaks ?? []).slice(0, 6).map((peak) => (
            <Box
              key={`${peak.midi}-${peak.hz}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '52px minmax(64px, 1fr) minmax(74px, 1fr)',
                gap: '8px',
                color: 'rgba(226, 234, 255, 0.78)',
                fontSize: '11px',
                marginTop: '6px',
              }}
            >
              <Box sx={{ color: '#F2F6FF', fontWeight: 900 }}>{peak.note}</Box>
              <Box>{formatNumber(peak.hz, 1)} Hz</Box>
              <Box>amp {formatNumber(peak.amp, 5)}</Box>
            </Box>
          ))}
          {!latestFrame && <Box sx={secondaryTextStyles}>No live peaks yet</Box>}
        </Box>

        <Box sx={summaryBoxStyles}>
          <Box sx={titleStyles}>Captured-window peaks</Box>
          {captureSummary.slice(0, 6).map((peak) => (
            <Box
              key={`${peak.midi}-${peak.hz}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '52px minmax(64px, 1fr) minmax(74px, 1fr)',
                gap: '8px',
                color: 'rgba(226, 234, 255, 0.78)',
                fontSize: '11px',
                marginTop: '6px',
              }}
            >
              <Box sx={{ color: '#F2F6FF', fontWeight: 900 }}>{peak.note}</Box>
              <Box>{formatNumber(peak.hz, 1)} Hz</Box>
              <Box>max {formatNumber(peak.maxAmp, 5)}</Box>
            </Box>
          ))}
          {captureSummary.length === 0 && (
            <Box sx={secondaryTextStyles}>No capture-stage peaks yet</Box>
          )}
        </Box>
      </Box>

      <Box sx={summaryBoxStyles}>
        <Box sx={{ ...titleStyles, marginBottom: '8px' }}>
          {viewState.captureFrames.length > 0 ? 'Capture heatmap' : 'Rolling heatmap'}
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <svg
            viewBox={`0 0 ${heatmapWidth} ${heatmapHeight}`}
            width="100%"
            height={heatmapHeight}
            role="img"
            aria-label="Calibration note amplitude heatmap"
            style={{ minWidth: 520, display: 'block' }}
          >
            <rect width={heatmapWidth} height={heatmapHeight} rx="10" fill="rgba(0,0,0,0.22)" />
            {heatmapRows.length === 0 && (
              <text
                x={heatmapWidth / 2}
                y={heatmapHeight / 2}
                textAnchor="middle"
                fill="rgba(226,234,255,0.68)"
                fontSize="13"
                fontWeight="700"
              >
                No peak heatmap yet
              </text>
            )}
            {heatmapRows.map((row, rowIndex) => {
              const y = topPadding + rowIndex * (cellHeight + cellGap);
              return (
                <React.Fragment key={row.midi}>
                  <text
                    x="10"
                    y={y + cellHeight * 0.68}
                    fill="#F2F6FF"
                    fontSize="11"
                    fontWeight="800"
                  >
                    {row.note}
                  </text>
                  {heatmapColumns.map((frame, columnIndex) => {
                    const amp = getPeakAmpForMidi(frame, row.midi);
                    const intensity = Math.max(0, Math.min(1, amp / heatmapMaxAmp));
                    const x = labelWidth + columnIndex * (cellWidth + cellGap);
                    return (
                      <rect
                        key={`${row.midi}-${frame.capturedAtMs}-${columnIndex}`}
                        x={x}
                        y={y}
                        width={cellWidth}
                        height={cellHeight}
                        rx="2"
                        fill={`rgba(121, 216, 255, ${0.08 + intensity * 0.88})`}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}
            {heatmapColumns.length > 0 && (
              <>
                <text
                  x={labelWidth}
                  y={heatmapHeight - 7}
                  fill="rgba(226,234,255,0.58)"
                  fontSize="10"
                >
                  older
                </text>
                <text
                  x={heatmapWidth - 52}
                  y={heatmapHeight - 7}
                  fill="rgba(226,234,255,0.58)"
                  fontSize="10"
                >
                  newer
                </text>
              </>
            )}
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

export default CalibrationDiagnosticsPanel;
